import React from 'react'
import PropTypes from 'prop-types'
import { withStyles } from '@material-ui/core'
import { withRouter } from 'react-router-dom'

import Modal from '../../uiKit/Modal'
import { HOME_PATH } from '../../configs'
import { alertError, alertSuccess } from '../../api'
import { applyBrandTheme } from '../../constants/qcsTheme'
import { setBrowserTabTitle } from '../../helpers/setBrowserTabTitle'
import {
  SUPERVISION_MODE,
  SUPERVISION_MODE_LABEL,
  SUPERVISION_MODE_OPTIONS,
} from '../../constants/supervisionMode'
import {
  createPackageAsDraft,
  updatePackage,
  patchStatus,
  deletePackage,
  getPackageEditImpact,
} from './api/packages'
import {
  ICON,
  SUPERVISION_MODE_DESC,
  PACKAGE_STATUS,
  makeEmptyPackage,
  fetchPackages,
  enabledCount,
  isTempPackageId,
  toPackageWriteBody,
  fromPackageResponse,
  syncPackageUiFlagsFromPkg,
  clearPackageUiFlags,
  upsertPackageInCache,
  removePackageFromCache,
  setPackagesCache,
  getCachedUsageLimitRange,
  apiStatusFromUi,
} from './helpers/packageTiers'

/**
 * Package builder — a full page (not a modal) for creating and editing the
 * packages an operator can assign to a QCS organisation.
 *
 * Left rail: search, "New package", and the list of packages. Right pane: the
 * selected package's name and its features. Tier level (Basic/Gold/Platinum) is
 * derived from how many features are on, so it is shown, never chosen.
 */

// The features a package can bundle. Kept here so the "X of N on" counter and
// the cards stay in step automatically.
const FEATURES = [
  {
    key: 'supervision',
    icon: ICON.users,
    title: 'Supervision',
    desc: 'Run supervision sessions with the professionals in this organisation.',
  },
  {
    key: 'carePlanAudit',
    icon: ICON.clipboardCheck,
    title: 'Care Plan Audit',
    desc: 'Audit care plans against the fundamental standards',
  },
]

const Glyph = ({ paths, size = 20, strokeWidth = 1.8 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    {paths.map((d, i) => (
      <path
        key={i}
        d={d}
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    ))}
  </svg>
)

Glyph.propTypes = { paths: PropTypes.array, size: PropTypes.number, strokeWidth: PropTypes.number }

// Side-by-side rail+pane stops fitting below this (matches OrganisationsTable).
const MOBILE_MQ = '(max-width: 900px)'

const styles = () => ({
  page: {
    // Bound the builder to the viewport so the rail and the editor pane scroll
    // internally and the footer bar stays pinned at the bottom, rather than the
    // whole document growing and scrolling as one.
    height: '100vh',
    overflow: 'hidden',
    // Light cool canvas for the editor pane; the rail is a mint panel (below).
    background: '#F6F9FB',
    fontFamily: 'var(--careco-font)',
    display: 'flex',
    flexDirection: 'column',
  },

  // Top bar — the app's standard detail-page pattern: sticky, "Back" on the
  // left, an uppercase eyebrow + bold title, actions on the right. Matches
  // AuditResult's TopBar so navigation feels the same across the app.
  header: {
    height: 64,
    flexShrink: 0,
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    padding: '0 28px',
    background: 'var(--careco-surface)',
    borderBottom: '1px solid var(--careco-border)',
    [`@media ${MOBILE_MQ}`]: {
      padding: '0 16px',
    },
  },
  // Narrow: hide rail or pane so only one panel is on screen (list ↔ detail).
  // !important: rail/pane/emptyPane set display:flex later in this sheet and would
  // otherwise win the cascade (same specificity), leaving both columns visible.
  panelHidden: {
    display: 'none !important',
  },
  // Narrow-only control to return from package detail to the list (not app Back).
  mobileBack: {
    display: 'none',
    [`@media ${MOBILE_MQ}`]: {
      display: 'inline-flex',
      alignItems: 'center',
      marginBottom: 16,
      border: 'none',
      background: 'transparent',
      padding: '6px 0',
      fontFamily: 'inherit',
      fontSize: 14,
      fontWeight: 600,
      color: 'var(--careco-text-secondary)',
      cursor: 'pointer',
      '&:hover': { color: 'var(--careco-text-primary)' },
      '&:focus-visible': { outline: '2px solid var(--careco-primary)', outlineOffset: 2 },
    },
  },
  mobileBackIcon: {
    display: 'inline-flex',
    marginRight: 8,
  },
  back: {
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    // Icon-to-"Back" spacing set with a margin on the icon (JSS drops flex gap).
    border: 'none',
    background: 'transparent',
    padding: '8px 10px',
    marginRight: 16,
    borderRadius: 'var(--careco-rounded-sm, 8px)',
    fontFamily: 'inherit',
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--careco-text-secondary)',
    cursor: 'pointer',
    '&:hover': { background: 'var(--careco-row-default)', color: 'var(--careco-text-primary)' },
    '&:focus-visible': { outline: '2px solid var(--careco-primary)', outlineOffset: 2 },
  },
  backIcon: {
    display: 'inline-flex',
    marginRight: 8,
  },
  topMeta: {
    display: 'flex',
    flexDirection: 'column',
    lineHeight: 1.2,
  },
  topEyebrow: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.6px',
    textTransform: 'uppercase',
    color: 'var(--careco-text-muted)',
  },
  topTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: 'var(--careco-text-primary)',
  },
  headerActions: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
  },
  // Push-to-live CTA (draft package) and the live badge / move-to-draft (live).
  pushLiveBtn: {
    height: 40,
    border: 'none',
    borderRadius: 'var(--careco-rounded-sm, 8px)',
    padding: '0 22px',
    fontFamily: 'inherit',
    fontSize: 14,
    fontWeight: 700,
    color: 'var(--color-white, #fff)',
    background: 'var(--careco-primary)',
    cursor: 'pointer',
    transition: 'background 120ms ease',
    '&:hover': { background: 'var(--careco-primary-hover)' },
    '&:focus-visible': { outline: '2px solid var(--careco-primary)', outlineOffset: 2 },
  },
  liveBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    marginRight: 16,
    fontSize: 13.5,
    fontWeight: 700,
    color: 'var(--careco-success)',
  },
  liveDot: {
    width: 8,
    height: 8,
    marginRight: 7,
    borderRadius: '50%',
    background: 'var(--careco-success)',
  },
  toDraftBtn: {
    height: 40,
    border: '1px solid var(--careco-border)',
    background: 'transparent',
    borderRadius: 'var(--careco-rounded-sm, 8px)',
    padding: '0 16px',
    fontFamily: 'inherit',
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--careco-text-secondary)',
    cursor: 'pointer',
    transition: 'color 120ms ease, border-color 120ms ease',
    '&:hover': { color: 'var(--careco-text-primary)', borderColor: 'var(--careco-primary)' },
  },
  // View mode — Edit affordance in the content header, read-only name/feature
  // display, and On/Off tags.
  editBtn: {
    marginLeft: 'auto',
    flexShrink: 0,
    alignSelf: 'center',
    height: 40,
    display: 'inline-flex',
    alignItems: 'center',
    border: '1px solid var(--careco-border)',
    background: 'var(--careco-surface)',
    borderRadius: 'var(--careco-rounded-sm, 8px)',
    padding: '0 20px',
    fontFamily: 'inherit',
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--careco-text-primary)',
    cursor: 'pointer',
    transition: 'border-color 120ms ease, color 120ms ease',
    '&:hover': { borderColor: 'var(--careco-primary)', color: 'var(--careco-primary)' },
  },
  editIcon: { display: 'inline-flex', marginRight: 8 },
  // Fixed action bar at the bottom of the right pane (a flex child that does not
  // scroll). Full-width; its inner content aligns with the centred pane content.
  footerBar: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    padding: '14px 6%',
    background: 'var(--careco-surface)',
    borderTop: '1px solid var(--careco-border)',
    [`@media ${MOBILE_MQ}`]: {
      flexWrap: 'wrap',
      padding: '12px 16px',
      rowGap: 10,
    },
  },
  footerHint: {
    fontSize: 13.5,
    color: 'var(--careco-text-muted)',
  },
  cancelEditBtn: {
    height: 46,
    marginRight: 12,
    border: '1px solid var(--careco-border)',
    background: 'transparent',
    borderRadius: 'var(--careco-rounded-sm, 8px)',
    padding: '0 22px',
    fontFamily: 'inherit',
    fontSize: 15,
    fontWeight: 600,
    color: 'var(--careco-text-secondary)',
    cursor: 'pointer',
    transition: 'color 120ms ease, border-color 120ms ease',
    '&:hover': { color: 'var(--careco-text-primary)', borderColor: 'var(--careco-primary)' },
  },
  viewLabel: {
    display: 'block',
    margin: '0 0 6px',
    fontSize: 12.5,
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: 'var(--careco-text-muted)',
  },
  viewValue: {
    margin: 0,
    fontSize: 18,
    fontWeight: 700,
    color: 'var(--careco-text-primary)',
  },
  statusTag: {
    flexShrink: 0,
    marginLeft: 16,
    padding: '4px 12px',
    borderRadius: 'var(--careco-rounded-pill, 999px)',
    fontSize: 12,
    fontWeight: 700,
  },
  statusOn: { background: 'var(--careco-primary-light)', color: 'var(--careco-primary)' },
  statusOff: { background: 'var(--careco-divider)', color: 'var(--careco-text-muted)' },
  viewDetail: {
    marginTop: 14,
    marginLeft: 60,
    paddingTop: 14,
    borderTop: '1px solid var(--careco-border)',
    fontSize: 13.5,
    color: 'var(--careco-text-secondary)',
  },
  discard: {
    border: 'none',
    background: 'transparent',
    padding: '0 4px',
    marginRight: 20,
    fontFamily: 'inherit',
    fontSize: 15,
    fontWeight: 600,
    color: 'var(--careco-text-secondary)',
    cursor: 'pointer',
    '&:hover': { color: 'var(--careco-text-primary)' },
    '&:disabled': { opacity: 0.4, cursor: 'default' },
  },
  save: {
    height: 46,
    border: 'none',
    borderRadius: 'var(--careco-rounded-sm, 8px)',
    padding: '0 26px',
    fontFamily: 'inherit',
    fontSize: 15,
    fontWeight: 600,
    color: 'var(--color-white, #fff)',
    background: 'var(--careco-primary)',
    cursor: 'pointer',
    '&:hover': { background: 'var(--careco-primary-hover)' },
    '&:disabled': { opacity: 0.5, cursor: 'default' },
  },

  body: {
    flex: 1,
    display: 'flex',
    minHeight: 0,
  },

  // Left rail — a mint column of elevated white cards, divided from the pane
  // by a right-hand stroke.
  rail: {
    width: 380,
    flexShrink: 0,
    boxSizing: 'border-box',
    padding: '24px 22px',
    background: '#F2FBF9',
    borderRight: '1px solid var(--careco-border)',
    display: 'flex',
    flexDirection: 'column',
    // No flex `gap` here — MUI v1's JSS drops it, so spacing is set with
    // explicit margins on the children below.
    minHeight: 0,
    overflowY: 'auto',
    [`@media ${MOBILE_MQ}`]: {
      // Full width only while this panel is the visible one (JS hides the other).
      width: '100%',
      flex: 1,
      borderRight: 'none',
      padding: '16px',
    },
  },
  searchWrap: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    // Clear, visible gap to the New package button (8–12px).
    marginBottom: 10,
  },
  searchIcon: {
    position: 'absolute',
    left: 18,
    color: 'var(--careco-text-muted)',
    display: 'inline-flex',
    pointerEvents: 'none',
  },
  // Matches the home-screen search: 48px bordered input, small radius, no
  // shadow, border highlights on hover/focus.
  search: {
    width: '100%',
    height: 48,
    boxSizing: 'border-box',
    padding: '0 18px 0 46px',
    border: '1px solid var(--careco-border)',
    borderRadius: 'var(--careco-rounded-sm, 8px)',
    background: 'var(--careco-surface)',
    fontFamily: 'inherit',
    fontSize: 14,
    color: 'var(--careco-text-primary)',
    outline: 'none',
    transition: 'border-color 120ms ease',
    '&::placeholder': { color: 'var(--careco-text-muted)' },
    '&:hover': { borderColor: 'var(--careco-primary)' },
    '&:focus': { borderColor: 'var(--careco-primary)' },
  },
  // Same bordered, small-radius treatment as the search field above.
  newBtn: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    height: 48,
    // Separates the button from the package list below (the rail's flex gap is
    // dropped by JSS).
    marginBottom: 18,
    padding: '0 18px',
    // (icon-to-text spacing is a margin on the icon below, not flex `gap`)
    border: '1px solid var(--careco-border)',
    borderRadius: 'var(--careco-rounded-sm, 8px)',
    background: 'var(--careco-surface)',
    fontFamily: 'inherit',
    fontSize: 14,
    fontWeight: 700,
    color: 'var(--careco-text-primary)',
    cursor: 'pointer',
    transition: 'border-color 120ms ease, background 120ms ease',
    '&:hover': { borderColor: 'var(--careco-primary)', background: 'var(--careco-row-default)' },
    '&:focus-visible': { outline: '2px solid var(--careco-primary)', outlineOffset: 2 },
  },
  newBtnIcon: {
    display: 'inline-flex',
    marginRight: 16,
  },
  // Divider + section header separating the search / New package controls from
  // the list of created packages.
  railDivider: {
    height: 1,
    flexShrink: 0,
    background: 'var(--careco-border)',
    margin: '0 0 18px',
  },
  railHeading: {
    margin: '0 0 12px',
    flexShrink: 0,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '0.6px',
    textTransform: 'uppercase',
    color: 'var(--careco-text-muted)',
  },
  // Draft / Live filter chips above the package list.
  chipRow: {
    display: 'flex',
    flexShrink: 0,
    marginBottom: 14,
  },
  chip: {
    flex: 1,
    height: 34,
    boxSizing: 'border-box',
    border: '1px solid var(--careco-border)',
    background: 'var(--careco-surface)',
    borderRadius: 'var(--careco-rounded-pill, 999px)',
    fontFamily: 'inherit',
    fontSize: 13,
    fontWeight: 700,
    color: 'var(--careco-text-secondary)',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'border-color 120ms ease, background 120ms ease, color 120ms ease',
    // Explicit gap between the two chips (JSS drops flex `gap`).
    '&:first-of-type': { marginRight: 8 },
    '&:hover': { borderColor: 'var(--careco-primary)' },
  },
  chipActive: {
    borderColor: 'var(--careco-primary)',
    background: 'var(--careco-primary-light)',
    color: 'var(--careco-primary)',
  },
  chipCount: {
    marginLeft: 6,
    fontSize: 12,
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums',
    opacity: 0.85,
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    gap: 12,
    margin: 0,
    padding: 0,
    listStyle: 'none',
  },
  // Elevated white card — no border; the selected one gets a primary ring.
  // Icon-to-text spacing is a margin on the icon (not flex `gap`, which JSS
  // drops), so it lands at an exact 16px.
  card: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    boxSizing: 'border-box',
    padding: '16px 16px',
    // Stroke line and corner radius matching the search field (bordered, flat,
    // no shadow).
    border: '1px solid var(--careco-border)',
    borderRadius: 'var(--careco-rounded-sm, 8px)',
    background: 'var(--careco-surface)',
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: 'inherit',
    transition: 'border-color 120ms ease, box-shadow 120ms ease',
    // Hover (non-selected only): the border highlights, like the search field.
    '&:not($cardActive):hover': {
      borderColor: 'var(--careco-primary)',
    },
    // Reveal the delete control on hover.
    '&:hover $cardDelete': { opacity: 1 },
  },
  cardActive: {
    borderColor: 'var(--careco-primary)',
    boxShadow: '0 0 0 1px var(--careco-primary) inset',
    background: 'var(--careco-surface)',
  },
  cardIcon: {
    flexShrink: 0,
    width: 34,
    height: 34,
    marginRight: 16,
    borderRadius: 'var(--careco-rounded-sm, 8px)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cardText: { minWidth: 0, flex: 1 },
  cardName: {
    margin: 0,
    fontSize: 14.5,
    fontWeight: 700,
    lineHeight: 1.3,
    color: 'var(--careco-text-primary)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  cardMeta: {
    margin: '3px 0 0',
    fontSize: 12.5,
    color: 'var(--careco-text-muted)',
  },
  cardDelete: {
    flexShrink: 0,
    border: 'none',
    background: 'transparent',
    color: 'var(--careco-text-muted)',
    cursor: 'pointer',
    display: 'inline-flex',
    padding: 4,
    borderRadius: 6,
    // Hidden until the card is hovered (or focused), to keep the list clean.
    opacity: 0,
    transition: 'opacity 120ms ease, color 120ms ease, background 120ms ease',
    '&:hover': { color: 'var(--careco-error)', background: 'rgba(0,0,0,0.04)' },
    '&:focus-visible': { opacity: 1, outline: '2px solid var(--careco-primary)', outlineOffset: 1 },
    // Touch devices have no hover — keep delete reachable on narrow viewports.
    [`@media ${MOBILE_MQ}`]: {
      opacity: 1,
    },
  },
  railEmpty: {
    padding: '20px 6px',
    textAlign: 'center',
    fontSize: 13.5,
    lineHeight: '20px',
    color: 'var(--careco-text-muted)',
  },

  // Right pane.
  // A flex column: a scrolling content area over a fixed footer bar, so the
  // footer (Publish to live / Save) stays put while the content scrolls.
  pane: {
    flex: 1,
    minWidth: 0,
    // Shrink below content height so paneScroll is the only vertical scroller
    // when Features toggles expand (default min-height:auto caused a second bar).
    minHeight: 0,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    [`@media ${MOBILE_MQ}`]: {
      width: '100%',
      flex: 1,
      minHeight: 0,
    },
  },
  paneScroll: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    // Top padding matches the rail so the two columns share a top baseline.
    padding: '24px 0 24px',
    [`@media ${MOBILE_MQ}`]: {
      padding: '16px 0',
    },
  },
  paneInner: {
    // Uses ~88% of the right panel (capped on ultra-wide screens), centred so
    // the left and right margins stay equal and comfortable — wider than the
    // old fixed 760px without going full-bleed.
    width: '88%',
    maxWidth: 1200,
    margin: '0 auto',
    [`@media ${MOBILE_MQ}`]: {
      width: '100%',
      boxSizing: 'border-box',
      padding: '0 16px',
    },
  },
  // Save action, below the features.
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 28,
  },
  // Content header — icon chip + title + subtext, above the name card.
  pageHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 24,
    [`@media ${MOBILE_MQ}`]: {
      flexWrap: 'wrap',
      rowGap: 12,
    },
  },
  pageHeaderIcon: {
    flexShrink: 0,
    width: 48,
    height: 48,
    marginRight: 16,
    borderRadius: 'var(--careco-rounded-md, 12px)',
    // Subtle tint (like the feature icons), not the solid primary used on the
    // Save CTA.
    background: 'var(--careco-primary-light)',
    color: 'var(--careco-primary)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  pageHeaderTitle: {
    margin: 0,
    fontSize: 22,
    fontWeight: 700,
    lineHeight: 1.2,
    color: 'var(--careco-text-primary)',
  },
  pageHeaderSub: {
    margin: '4px 0 0',
    fontSize: 14,
    lineHeight: '20px',
    color: 'var(--careco-text-muted)',
  },
  // Name card — the drop-shadow container.
  nameCard: {
    background: 'var(--careco-surface)',
    border: '1px solid var(--careco-border)',
    borderRadius: 'var(--careco-rounded-lg, 16px)',
    boxShadow: '0 10px 30px rgba(19, 69, 186, 0.06)',
    padding: '26px 28px',
  },
  fieldLabel: {
    display: 'block',
    margin: '0 0 8px',
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--careco-text-primary)',
  },
  req: { color: 'var(--careco-error)', marginLeft: 2 },
  errorText: {
    margin: '8px 0 0',
    fontSize: 12.5,
    color: 'var(--careco-error)',
  },
  nameInput: {
    width: '100%',
    height: 52,
    boxSizing: 'border-box',
    padding: '0 16px',
    border: '1px solid var(--careco-border)',
    borderRadius: 'var(--careco-rounded-sm, 8px)',
    fontFamily: 'inherit',
    fontSize: 16,
    fontWeight: 600,
    color: 'var(--careco-text-primary)',
    outline: 'none',
    '&::placeholder': { color: 'var(--careco-text-muted)', fontWeight: 400 },
    '&:hover': { borderColor: 'var(--careco-primary)' },
    '&:focus': { borderColor: 'var(--careco-primary)' },
  },
  nameInputError: {
    borderColor: 'var(--careco-error)',
    '&:hover': { borderColor: 'var(--careco-error)' },
    '&:focus': { borderColor: 'var(--careco-error)' },
  },

  // Features section.
  featuresHead: {
    display: 'flex',
    alignItems: 'baseline',
    marginTop: 34,
  },
  featuresLabel: {
    margin: 0,
    fontSize: 12.5,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'var(--careco-text-muted)',
  },
  onCount: {
    marginLeft: 'auto',
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--careco-text-muted)',
    fontVariantNumeric: 'tabular-nums',
  },
  featuresSub: {
    margin: '6px 0 0',
    fontSize: 14,
    color: 'var(--careco-text-secondary)',
  },
  featureCard: {
    marginTop: 16,
    border: '1px solid var(--careco-border)',
    borderRadius: 'var(--careco-rounded-md, 12px)',
    background: 'var(--careco-surface)',
    padding: '18px 20px',
    transition: 'border-color 140ms ease',
  },
  featureCardOn: { borderColor: 'var(--careco-primary)' },
  featureHead: { display: 'flex', alignItems: 'center' },
  featIcon: {
    flexShrink: 0,
    width: 44,
    height: 44,
    marginRight: 16,
    borderRadius: 'var(--careco-rounded-md, 12px)',
    background: 'var(--careco-primary-light)',
    color: 'var(--careco-primary)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  featText: { minWidth: 0, flex: 1 },
  featTitle: {
    margin: 0,
    fontSize: 16,
    fontWeight: 700,
    lineHeight: 1.3,
    color: 'var(--careco-text-primary)',
  },
  featDesc: {
    margin: '3px 0 0',
    fontSize: 14,
    lineHeight: '20px',
    color: 'var(--careco-text-muted)',
  },

  // Toggle switch.
  switchLabel: {
    flexShrink: 0,
    marginLeft: 16,
    display: 'inline-flex',
    cursor: 'pointer',
    position: 'relative',
  },
  hiddenInput: {
    position: 'absolute',
    opacity: 0,
    width: 1,
    height: 1,
    margin: 0,
    left: 0,
    top: 0,
    pointerEvents: 'none',
  },
  track: {
    width: 46,
    height: 26,
    borderRadius: 999,
    background: 'var(--careco-border)',
    position: 'relative',
    transition: 'background 160ms ease',
  },
  trackOn: { background: 'var(--careco-primary)' },
  knob: {
    position: 'absolute',
    top: 3,
    left: 3,
    width: 20,
    height: 20,
    borderRadius: '50%',
    background: 'var(--color-white, #fff)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
    transition: 'transform 160ms ease',
  },
  knobOn: { transform: 'translateX(20px)' },

  // Sub-options (revealed when a feature is on).
  subRow: {
    display: 'flex',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    marginTop: 16,
    marginLeft: 60,
    paddingTop: 16,
    borderTop: '1px solid var(--careco-border)',
  },
  subLabel: {
    width: '100%',
    margin: '0 0 12px',
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--careco-text-secondary)',
  },
  // Two equal columns, side by side. The 16px column gap is applied inline
  // (JSS drops the `gap` property); grid keeps both cards the same height.
  modeList: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    width: '100%',
  },
  modeCard: {
    display: 'flex',
    alignItems: 'flex-start',
    width: '100%',
    textAlign: 'left',
    padding: '12px 14px',
    border: '1px solid var(--careco-border)',
    borderRadius: 'var(--careco-rounded-sm, 8px)',
    background: 'var(--careco-surface)',
    fontFamily: 'inherit',
    cursor: 'pointer',
    transition: 'border-color 140ms ease, background 140ms ease',
    '&:hover': { borderColor: 'var(--careco-primary)' },
  },
  modeCardOn: { borderColor: 'var(--careco-primary)', background: 'var(--careco-primary-light)' },
  modeName: {
    display: 'block',
    fontSize: 14,
    fontWeight: 700,
    lineHeight: 1.3,
    color: 'var(--careco-text-primary)',
  },
  modeDesc: {
    display: 'block',
    marginTop: 3,
    fontSize: 12.5,
    lineHeight: '17px',
    color: 'var(--careco-text-muted)',
  },
  box: {
    flexShrink: 0,
    marginRight: 12,
    marginTop: 1,
    width: 18,
    height: 18,
    boxSizing: 'border-box',
    border: '1.5px solid var(--careco-border)',
    borderRadius: 5,
    background: 'var(--careco-surface)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--color-white, #fff)',
    transition: 'background 120ms ease, border-color 120ms ease',
  },
  boxChecked: { background: 'var(--careco-primary)', borderColor: 'var(--careco-primary)' },

  // Care Plan Audit's monthly-limit row: one horizontal flex row, everything on
  // the same vertical centre line (unlike the flex-start `subRow`).
  limitRow: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 16,
    marginLeft: 60,
    paddingTop: 16,
    borderTop: '1px solid var(--careco-border)',
  },
  limitLabel: { marginRight: 32, fontSize: 13.5, fontWeight: 600, color: 'var(--careco-text-secondary)' },
  limitError: { marginLeft: 60 },
  stepper: {
    display: 'inline-flex',
    alignItems: 'stretch',
    height: 42,
    border: '1px solid var(--careco-border)',
    borderRadius: 'var(--careco-rounded-sm, 8px)',
    background: 'var(--careco-surface)',
    overflow: 'hidden',
  },
  stepperDisabled: { background: 'var(--careco-row-default)' },
  stepBtn: {
    width: 40,
    border: 'none',
    background: 'transparent',
    color: 'var(--careco-primary)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    '&:hover': { background: 'var(--careco-row-default)' },
    '&:disabled': { color: 'var(--careco-text-muted)', cursor: 'default', background: 'transparent' },
  },
  stepInput: {
    width: 56,
    border: 'none',
    borderLeft: '1px solid var(--careco-border)',
    borderRight: '1px solid var(--careco-border)',
    textAlign: 'center',
    fontFamily: 'inherit',
    fontSize: 15,
    fontWeight: 600,
    color: 'var(--careco-text-primary)',
    background: 'transparent',
    outline: 'none',
    '&:disabled': { color: 'var(--careco-text-muted)' },
  },
  unlimited: {
    display: 'inline-flex',
    alignItems: 'center',
    marginLeft: 36,
    fontSize: 14,
    color: 'var(--careco-text-secondary)',
    cursor: 'pointer',
    userSelect: 'none',
    // The shared checkbox carries a 1px top nudge for the (top-aligned) mode
    // cards; here the row is centre-aligned, so remove it for a true centre.
    '& $box': { marginTop: 0, marginRight: 12 },
  },

  // Empty state (no package selected).
  emptyPane: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--careco-text-muted)',
    textAlign: 'center',
    padding: 40,
    [`@media ${MOBILE_MQ}`]: {
      width: '100%',
      flex: 1,
      padding: 24,
    },
  },
  emptyTitle: { margin: '0 0 8px', fontSize: 22, fontWeight: 700, color: 'var(--careco-text-primary)' },
  emptyText: { margin: 0, fontSize: 15, lineHeight: '22px', maxWidth: 360 },
  // Delete confirmation dialog — same header / message / two-button structure
  // as the app's other delete modals (e.g. SupervisionDeleteModal).
  confirmPaper: {
    width: 440,
    maxWidth: '100%',
    padding: '28px 28px 22px',
  },
  confirmBody: { fontFamily: 'var(--careco-font)' },
  confirmHeader: {
    margin: 0,
    fontSize: 20,
    fontWeight: 700,
    lineHeight: 1.25,
    color: 'var(--careco-text-primary)',
  },
  confirmText: {
    margin: '12px 0 0',
    fontSize: 14,
    lineHeight: '21px',
    color: 'var(--careco-text-secondary)',
  },
  confirmActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 24,
    paddingTop: 20,
    borderTop: '1px solid var(--careco-border)',
  },
  confirmCancel: {
    height: 44,
    border: '1px solid var(--careco-border)',
    background: 'transparent',
    borderRadius: 'var(--careco-rounded-sm, 8px)',
    padding: '0 22px',
    fontFamily: 'inherit',
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--careco-text-primary)',
    cursor: 'pointer',
    '&:hover': { background: 'var(--careco-row-default)' },
  },
  confirmDelete: {
    marginLeft: 12,
    height: 44,
    border: 'none',
    borderRadius: 'var(--careco-rounded-sm, 8px)',
    padding: '0 22px',
    fontFamily: 'inherit',
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--color-white, #fff)',
    background: 'var(--careco-error)',
    cursor: 'pointer',
    '&:hover': { opacity: 0.92 },
  },
  confirmPrimary: {
    marginLeft: 12,
    height: 44,
    border: 'none',
    borderRadius: 'var(--careco-rounded-sm, 8px)',
    padding: '0 22px',
    fontFamily: 'inherit',
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--color-white, #fff)',
    background: 'var(--careco-primary)',
    cursor: 'pointer',
    '&:hover': { background: 'var(--careco-primary-hover)' },
    '&:disabled': { opacity: 0.55, cursor: 'default' },
  },
  impactPaper: {
    width: 520,
    maxWidth: '100%',
    padding: '28px 28px 22px',
    display: 'flex',
    flexDirection: 'column',
  },
  // Header and footer stay put; only impactScroll scrolls.
  impactBody: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    minHeight: 0,
  },
  impactScroll: {
    flex: '1 1 auto',
    minHeight: 0,
    overflowY: 'auto',
    // Run the scrollbar to the card edge, like Manage Limits; the padding puts the text back.
    margin: '0 -28px',
    padding: '0 28px',
  },
  impactSection: { marginTop: 16 },
  impactSectionTitle: {
    margin: '0 0 8px',
    fontSize: 13,
    fontWeight: 700,
    color: 'var(--careco-text-primary)',
  },
  impactList: {
    margin: 0,
    padding: '0 0 0 18px',
    fontSize: 13.5,
    lineHeight: '20px',
    color: 'var(--careco-text-secondary)',
  },
  impactWarn: {
    margin: '12px 0 0',
    padding: '10px 12px',
    borderRadius: 'var(--careco-rounded-sm, 8px)',
    background: 'var(--careco-error-light, #fde8e8)',
    color: 'var(--careco-error)',
    fontSize: 13,
    lineHeight: '18px',
  },
  impactMuted: {
    margin: '8px 0 0',
    fontSize: 13,
    color: 'var(--careco-text-muted)',
  },
  emptyBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 10,
    marginTop: 24,
    height: 50,
    padding: '0 26px',
    border: 'none',
    borderRadius: 'var(--careco-rounded-sm, 8px)',
    background: 'var(--careco-primary)',
    color: 'var(--color-white, #fff)',
    fontFamily: 'inherit',
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    '&:hover': { background: 'var(--careco-primary-hover)' },
  },
})

class PackageBuilder extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      packages: [],
      selectedId: null,
      search: '',
      dirty: false,
      nameError: false,
      // A package with no features on is not a package — block the save.
      featureError: false,
      // True once at least one package has been persisted via the API.
      savedExists: false,
      // Ids already persisted on the server. Drives the CTA: persisted → "Update",
      // temp / new → "Save".
      savedIds: [],
      // Id of the package pending a delete confirmation.
      deleteId: null,
      // Which status the left list is showing — Draft or Live.
      statusFilter: PACKAGE_STATUS.DRAFT,
      // Right pane mode: 'view' (read-only details) or 'edit' (the form).
      mode: 'edit',
      loading: true,
      saving: false,
      loadError: null,
      // Pending PUT after edit-impact warning (existing packages only).
      editImpact: null,
      pendingUpdateBody: null,
      impactLoading: false,
      // Viewport ≤900px — list ↔ detail instead of side-by-side.
      isNarrow: typeof window !== 'undefined' && window.matchMedia(MOBILE_MQ).matches,
      // Narrow only: true = package pane, false = package list.
      mobileShowDetail: false,
    }
  }

  componentDidMount() {
    // Opened from the QCS home, so it keeps the QCS green/teal palette to match
    // the mint rail and the surface it launches from.
    applyBrandTheme(true)
    setBrowserTabTitle('Package builder')
    this._mobileMq =
      typeof window !== 'undefined' && window.matchMedia
        ? window.matchMedia(MOBILE_MQ)
        : null
    this._onMobileMq = event => {
      const isNarrow = Boolean(event.matches)
      this.setState(prev => ({
        isNarrow,
        // Keep editor+footer open when a package is already selected; otherwise list.
        mobileShowDetail: isNarrow ? Boolean(prev.selectedId) : prev.mobileShowDetail,
      }))
    }
    if (this._mobileMq) {
      if (this._mobileMq.addEventListener) {
        this._mobileMq.addEventListener('change', this._onMobileMq)
      } else if (this._mobileMq.addListener) {
        this._mobileMq.addListener(this._onMobileMq)
      }
      this.setState({ isNarrow: this._mobileMq.matches })
    }
    this.loadFromApi()
  }

  componentWillUnmount() {
    // Leaving the QCS area returns to the default CareBrain palette.
    applyBrandTheme(false)
    if (this._loadAbort) this._loadAbort.abort()
    if (this._mobileMq && this._onMobileMq) {
      if (this._mobileMq.removeEventListener) {
        this._mobileMq.removeEventListener('change', this._onMobileMq)
      } else if (this._mobileMq.removeListener) {
        this._mobileMq.removeListener(this._onMobileMq)
      }
    }
  }

  loadFromApi = () => {
    if (this._loadAbort) this._loadAbort.abort()
    this._loadAbort = typeof AbortController !== 'undefined' ? new AbortController() : null
    const signal = this._loadAbort?.signal

    this.setState({ loading: true, loadError: null })

    fetchPackages({ signal })
      .then(packages => {
        const first = packages[0] || null
        this.setState(prev => ({
          packages,
          selectedId: first ? first.id : null,
          statusFilter: first?.status || PACKAGE_STATUS.DRAFT,
          savedExists: packages.length > 0,
          savedIds: packages.map(p => p.id),
          mode: first ? 'view' : 'edit',
          dirty: false,
          loading: false,
          loadError: null,
          // Narrow + a package: open detail so paneScroll/footerBar are reachable.
          mobileShowDetail: prev.isNarrow && Boolean(first),
        }))
      })
      .catch(err => {
        if (err?.name === 'AbortError') return
        this.setState({
          loading: false,
          loadError: err?.message || 'Could not load packages.',
        })
        alertError(err?.message || 'Could not load packages. Please try again or ping support.')
      })
  }

  goBack = () => this.props.history.push(`${HOME_PATH}/qcs`)

  // Narrow: leave detail and show the package list (does not leave the builder).
  backToMobileList = () => this.setState({ mobileShowDetail: false })

  selectPackage = id =>
    this.setState({ selectedId: id, mode: 'view', mobileShowDetail: true })

  selected = () =>
    this.state.packages.find(p => String(p.id) === String(this.state.selectedId)) || null

  patchSelected = patch =>
    this.setState(prev => {
      const packages = prev.packages.map(p =>
        (String(p.id) === String(prev.selectedId) ? { ...p, ...patch } : p),
      )
      const sel = packages.find(p => String(p.id) === String(prev.selectedId))
      return {
        dirty: true,
        // Typing into the (required) name field clears its error.
        nameError: 'name' in patch && String(patch.name).trim() ? false : prev.nameError,
        // Turning a feature on clears the "needs a feature" error.
        featureError: sel && enabledCount(sel) > 0 ? false : prev.featureError,
        packages,
      }
    })

  addPackage = () => {
    const pkg = makeEmptyPackage()
    this.setState(prev => ({
      packages: [...prev.packages, pkg],
      selectedId: pkg.id,
      dirty: true,
      // A new package is a draft — show the Draft list so it is visible.
      statusFilter: PACKAGE_STATUS.DRAFT,
      // Open a new package straight in the edit form.
      mode: 'edit',
      mobileShowDetail: true,
    }))
  }

  // Move the selected package between Draft and Live via PATCH /status.
  // Pushing to live first validates it (a live package must be complete).
  setStatus = status => {
    const pkg = this.selected()
    if (!pkg) return
    if (isTempPackageId(pkg.id)) {
      alertError('Save the package before changing its status.')
      return
    }
    if (status === PACKAGE_STATUS.LIVE) {
      // Publish lives on the view pane — jump into edit so the field errors are
      // visible instead of looking like a dead button.
      if (!(pkg.name || '').trim()) {
        this.setState({ nameError: true, mode: 'edit' })
        return
      }
      if (enabledCount(pkg) === 0) {
        this.setState({ featureError: true, mode: 'edit' })
        return
      }
      if (pkg.supervision && !(pkg.supervisionModes || []).length) {
        this.setState({ mode: 'edit' })
        alertError('Choose at least one session type before publishing.')
        return
      }
    }

    this.setState({ saving: true })
    patchStatus(pkg.id, apiStatusFromUi(status))
      .then(({ data, message }) => {
        const mapped = data ? fromPackageResponse(data) : { ...pkg, status }
        const packages = this.state.packages.map(p => (p.id === pkg.id ? mapped : p))
        setPackagesCache(packages.filter(p => !isTempPackageId(p.id)))
        upsertPackageInCache(mapped)
        this.setState({
          packages,
          dirty: false,
          nameError: false,
          featureError: false,
          savedExists: true,
          savedIds: packages.filter(p => !isTempPackageId(p.id)).map(p => p.id),
          statusFilter: mapped.status,
          saving: false,
        })
        const name = (mapped.name || '').trim() || 'Package'
        alertSuccess(
          message ||
            (status === PACKAGE_STATUS.LIVE
              ? `“${name}” pushed to live.`
              : `“${name}” moved back to draft.`),
        )
      })
      .catch(err => {
        this.setState({ saving: false })
        alertError(err?.message || 'Could not update package status.')
      })
  }

  requestDelete = (id, event) => {
    event.stopPropagation()
    this.setState({ deleteId: id })
  }

  cancelDelete = () => this.setState({ deleteId: null })

  confirmDelete = () => {
    const id = this.state.deleteId
    if (id == null) return

    const finishLocalRemove = () => {
      clearPackageUiFlags(id)
      removePackageFromCache(id)
      this.setState(prev => {
        const packages = prev.packages.filter(p => p.id !== id)
        setPackagesCache(packages.filter(p => !isTempPackageId(p.id)))
        const selectedId =
          prev.selectedId === id ? (packages[0] ? packages[0].id : null) : prev.selectedId
        return {
          packages,
          selectedId,
          dirty: false,
          deleteId: null,
          savedIds: prev.savedIds.filter(sid => sid !== id),
          savedExists: packages.some(p => !isTempPackageId(p.id)),
          mode: selectedId ? prev.mode : 'edit',
          saving: false,
        }
      })
    }

    // Never-saved drafts only live in memory.
    if (isTempPackageId(id)) {
      finishLocalRemove()
      return
    }

    this.setState({ saving: true })
    deletePackage(id)
      .then(({ message }) => {
        finishLocalRemove()
        alertSuccess(message || 'Package deleted successfully.')
      })
      .catch(err => {
        this.setState({ saving: false, deleteId: null })
        // 409 "still referenced" — keep the row and show the server message.
        alertError(err?.message || 'Could not delete this package.')
      })
  }

  toggleMode = mode =>
    this.setState(prev => {
      let changed = false
      const packages = prev.packages.map(p => {
        if (p.id !== prev.selectedId) return p
        const set = new Set(p.supervisionModes || [])
        if (set.has(mode)) {
          set.delete(mode)
          // Dynamic requires Default, so clearing Default also clears Dynamic.
          if (mode === SUPERVISION_MODE.DEFAULT) set.delete(SUPERVISION_MODE.DYNAMIC)
          // Supervision requires at least one session type — refuse emptying the set.
          if (set.size === 0) return p
        } else {
          set.add(mode)
          // Dynamic implies Default — selecting Dynamic auto-selects Default.
          if (mode === SUPERVISION_MODE.DYNAMIC) set.add(SUPERVISION_MODE.DEFAULT)
        }
        changed = true
        // Keep the stored order stable (Default before Dynamic).
        return { ...p, supervisionModes: SUPERVISION_MODE_OPTIONS.filter(m => set.has(m)) }
      })
      // No-op click (e.g. unchecking the last remaining mode) must not mark dirty.
      if (!changed) return null
      return { dirty: true, packages }
    })

  stepLimit = delta => {
    const pkg = this.selected()
    if (!pkg) return
    const { min, max } = getCachedUsageLimitRange()
    const next = Math.min(max, Math.max(min, (Number(pkg.auditLimit) || 0) + delta))
    this.patchSelected({ auditLimit: String(next) })
  }

  handleSave = () => {
    const pkg = this.selected()
    if (!pkg) return

    if (!(pkg.name || '').trim()) {
      this.setState({ nameError: true })
      return
    }
    if (enabledCount(pkg) === 0) {
      this.setState({ featureError: true })
      return
    }
    if (pkg.supervision && !(pkg.supervisionModes || []).length) {
      alertError('Choose at least one session type before saving.')
      return
    }
    const { min: limitMin, max: limitMax } = getCachedUsageLimitRange()
    const limitNum = Number(pkg.auditLimit)
    if (
      pkg.carePlanAudit &&
      !pkg.auditUnlimited &&
      (String(pkg.auditLimit ?? '') === '' || Number.isNaN(limitNum) || limitNum < limitMin || limitNum > limitMax)
    ) {
      return
    }

    const body = toPackageWriteBody(pkg, getCachedUsageLimitRange())
    const isNew = isTempPackageId(pkg.id)

    // New package: Save = draft. No edit-impact (nothing assigned yet).
    if (isNew) {
      this.setState({ saving: true })
      createPackageAsDraft(body)
        .then(({ data, message }) => this.applySaveSuccess(pkg, data, message, true))
        .catch(err => {
          this.setState({ saving: false })
          alertError(err?.message || 'Could not save the package.')
        })
      return
    }

    // Update: fetch impact first. Skip confirm modal when nothing is assigned.
    this.setState({ impactLoading: true, saving: true })
    getPackageEditImpact(pkg.id)
      .then(impact => {
        const botsToUpdate = impact.botsToUpdate || []
        const botsToSkip = impact.botsToSkip || []
        const assignedCount =
          typeof impact.assignedBotCount === 'number'
            ? impact.assignedBotCount
            : botsToUpdate.length + botsToSkip.length

        if (assignedCount === 0) {
          this.setState({ impactLoading: false })
          this.performPackageUpdate(pkg, body)
          return
        }

        this.setState({
          editImpact: impact,
          pendingUpdateBody: body,
          impactLoading: false,
          saving: false,
        })
      })
      .catch(err => {
        this.setState({ impactLoading: false, saving: false })
        alertError(err?.message || 'Could not load package edit impact.')
      })
  }

  cancelEditImpact = () => {
    this.setState({ editImpact: null, pendingUpdateBody: null })
  }

  performPackageUpdate = (pkg, body) => {
    this.setState({ saving: true })
    updatePackage(pkg.id, body)
      .then(({ data, message }) => this.applySaveSuccess(pkg, data, message, false))
      .catch(err => {
        this.setState({ saving: false })
        alertError(err?.message || 'Could not save the package.')
      })
  }

  confirmEditImpact = () => {
    const pkg = this.selected()
    const body = this.state.pendingUpdateBody
    if (!pkg || !body) {
      this.cancelEditImpact()
      return
    }
    this.performPackageUpdate(pkg, body)
  }

  applySaveSuccess = (pkg, data, message, isNew) => {
    if (!data) throw new Error(message || 'Package save returned no data.')
    syncPackageUiFlagsFromPkg({ ...pkg, id: String(data.id) })
    const withFlags = fromPackageResponse(data)

    this.setState(prev => {
      const packages = prev.packages.map(p => (p.id === pkg.id ? withFlags : p))
      setPackagesCache(packages.filter(p => !isTempPackageId(p.id)))
      upsertPackageInCache(withFlags)
      return {
        packages,
        selectedId: withFlags.id,
        dirty: false,
        nameError: false,
        featureError: false,
        savedExists: true,
        savedIds: Array.from(new Set([...prev.savedIds.filter(id => id !== pkg.id), withFlags.id])),
        statusFilter: withFlags.status,
        mode: 'view',
        saving: false,
        editImpact: null,
        pendingUpdateBody: null,
      }
    })
    alertSuccess(
      message || (isNew ? 'Package saved as draft.' : 'Package updated successfully.'),
    )
  }

  handleDiscard = () => {
    const selectedTemp = isTempPackageId(this.state.selectedId)
      ? this.state.packages.find(p => p.id === this.state.selectedId)
      : null
    fetchPackages()
      .then(packages => {
        const merged = selectedTemp ? [...packages, selectedTemp] : packages
        this.setState(prev => {
          let selectedId = null
          if (packages.find(p => p.id === prev.selectedId)) {
            selectedId = prev.selectedId
          } else if (selectedTemp) {
            selectedId = selectedTemp.id
          } else if (packages[0]) {
            selectedId = packages[0].id
          }
          return { packages: merged, selectedId, dirty: false }
        })
      })
      .catch(err => alertError(err?.message || 'Could not reload packages.'))
  }

  // Leave the edit form without saving: discard unsaved edits (reload from
  // API) and return to the view. A brand-new, never-saved package is not on
  // the server, so it drops away and the selection falls back to the first package.
  // On narrow, also return to the list (rail) instead of opening that first package.
  cancelEdit = () => {
    const selectedId = this.state.selectedId
    if (isTempPackageId(selectedId)) {
      this.setState(prev => {
        const packages = prev.packages.filter(p => p.id !== selectedId)
        const nextId = packages[0] ? packages[0].id : null
        return {
          packages,
          selectedId: nextId,
          dirty: false,
          nameError: false,
          featureError: false,
          mode: nextId ? 'view' : 'edit',
          ...(prev.isNarrow ? { mobileShowDetail: false } : null),
        }
      })
      return
    }

    fetchPackages()
      .then(packages => {
        this.setState(prev => {
          const stillExists = packages.find(p => p.id === prev.selectedId)
          return {
            packages,
            selectedId: stillExists ? prev.selectedId : packages[0] ? packages[0].id : null,
            dirty: false,
            nameError: false,
            featureError: false,
            mode: 'view',
          }
        })
      })
      .catch(err => alertError(err?.message || 'Could not reload packages.'))
  }

  renderFeatureToggle(on, onChange) {
    const { classes } = this.props
    return (
      <label className={classes.switchLabel}>
        <input
          type="checkbox"
          className={classes.hiddenInput}
          checked={on}
          onChange={e => onChange(e.target.checked)}
        />
        <span className={`${classes.track} ${on ? classes.trackOn : ''}`} aria-hidden="true">
          <span className={`${classes.knob} ${on ? classes.knobOn : ''}`} />
        </span>
      </label>
    )
  }

  // Read-only view of a saved package: the details, an Edit affordance, and the
  // Publish-to-live (or Live / Move-to-draft) control.
  renderView(pkg) {
    const { classes } = this.props
    const onCount = enabledCount(pkg)
    const isLive = pkg.status === PACKAGE_STATUS.LIVE

    return (
      <React.Fragment>
        <div className={classes.paneScroll}>
        <div className={classes.paneInner}>
          <button
            type="button"
            className={classes.mobileBack}
            onClick={this.backToMobileList}>
            <span className={classes.mobileBackIcon}>
              <Glyph paths={ICON.arrowLeft} size={16} />
            </span>
            Packages
          </button>
          <div className={classes.pageHeader}>
            <span className={classes.pageHeaderIcon}>
              <Glyph paths={ICON.box} size={26} />
            </span>
            <div>
              <h1 className={classes.pageHeaderTitle}>Package details</h1>
              <p className={classes.pageHeaderSub}>Review this package, then publish it to live.</p>
            </div>
            <button
              type="button"
              className={classes.editBtn}
              onClick={() => this.setState({ mode: 'edit' })}>
              <span className={classes.editIcon}>
                <Glyph paths={ICON.pencil} size={16} />
              </span>
              Edit
            </button>
          </div>

          <div className={classes.nameCard}>
            <span className={classes.viewLabel}>Tier name</span>
            <p className={classes.viewValue}>{pkg.name || 'Untitled package'}</p>
          </div>

          <div className={classes.featuresHead}>
            <p className={classes.featuresLabel}>Features</p>
            <span className={classes.onCount}>
              {onCount} of {FEATURES.length} on
            </span>
          </div>

          {FEATURES.map(feature => {
            const on = Boolean(pkg[feature.key])
            return (
              <div
                key={feature.key}
                className={`${classes.featureCard} ${on ? classes.featureCardOn : ''}`}>
                <div className={classes.featureHead}>
                  <span className={classes.featIcon}>
                    <Glyph paths={feature.icon} />
                  </span>
                  <div className={classes.featText}>
                    <h4 className={classes.featTitle}>{feature.title}</h4>
                    <p className={classes.featDesc}>{feature.desc}</p>
                  </div>
                </div>

                {feature.key === 'supervision' && on && (
                  <div className={classes.viewDetail}>
                    Session type:{' '}
                    {(pkg.supervisionModes || []).map(m => SUPERVISION_MODE_LABEL[m]).join(' + ') ||
                      '—'}
                  </div>
                )}
                {feature.key === 'carePlanAudit' && on && (
                  <div className={classes.viewDetail}>
                    Monthly limit:{' '}
                    {pkg.auditUnlimited ? 'Unlimited' : `${pkg.auditLimit || 0} / month`}
                  </div>
                )}
              </div>
            )
          })}
        </div>
        </div>

        {/* Fixed action bar at the bottom of the right pane. */}
        <div className={classes.footerBar} style={{ justifyContent: 'space-between' }}>
          {isLive ? (
            <React.Fragment>
              <span className={classes.liveBadge}>
                <span className={classes.liveDot} />
                Live
              </span>
              <button
                type="button"
                className={classes.toDraftBtn}
                onClick={() => this.setStatus(PACKAGE_STATUS.DRAFT)}>
                Move to draft
              </button>
            </React.Fragment>
          ) : (
            <React.Fragment>
              <span className={classes.footerHint}>This package is a draft.</span>
              <button
                type="button"
                className={classes.pushLiveBtn}
                onClick={() => this.setStatus(PACKAGE_STATUS.LIVE)}>
                Publish to live
              </button>
            </React.Fragment>
          )}
        </div>
      </React.Fragment>
    )
  }

  renderEditor(pkg) {
    const { classes } = this.props
    const onCount = enabledCount(pkg)
    const { min: limitMin, max: limitMax } = getCachedUsageLimitRange()
    const auditValue = String(pkg.auditLimit ?? '')
    const auditNum = Number(auditValue)
    const auditChecked = pkg.carePlanAudit && !pkg.auditUnlimited
    const auditOutOfRange =
      auditChecked &&
      auditValue !== '' &&
      !Number.isNaN(auditNum) &&
      (auditNum < limitMin || auditNum > limitMax)
    const auditLimitInvalid =
      auditChecked && (auditValue === '' || Number.isNaN(auditNum) || auditOutOfRange)

    return (
      <React.Fragment>
        <div className={classes.paneScroll}>
        <div className={classes.paneInner}>
        <button
          type="button"
          className={classes.mobileBack}
          onClick={this.backToMobileList}>
          <span className={classes.mobileBackIcon}>
            <Glyph paths={ICON.arrowLeft} size={16} />
          </span>
          Packages
        </button>
        <div className={classes.pageHeader}>
          <span className={classes.pageHeaderIcon}>
            <Glyph paths={ICON.box} size={26} />
          </span>
          <div>
            <h1 className={classes.pageHeaderTitle}>Package details</h1>
            <p className={classes.pageHeaderSub}>
              Give the package a name and switch on the features it includes.
            </p>
          </div>
        </div>

        <div className={classes.nameCard}>
          <label className={classes.fieldLabel} htmlFor="pkg-name">
            Tier name<span className={classes.req}>*</span>
          </label>
          <input
            id="pkg-name"
            className={`${classes.nameInput} ${
              this.state.nameError && !pkg.name.trim() ? classes.nameInputError : ''
            }`}
            type="text"
            value={pkg.name}
            placeholder="Name this tier"
            maxLength={80}
            aria-required="true"
            aria-invalid={this.state.nameError && !pkg.name.trim()}
            onChange={e => this.patchSelected({ name: e.target.value })}
          />
          {this.state.nameError && !pkg.name.trim() && (
            <p className={classes.errorText}>Tier name is required.</p>
          )}
        </div>

        <div className={classes.featuresHead}>
          <p className={classes.featuresLabel}>Features</p>
          <span className={classes.onCount}>
            {onCount} of {FEATURES.length} on
          </span>
        </div>
        {this.state.featureError && onCount === 0 && (
          <p className={classes.errorText}>Switch on at least one feature to save this package.</p>
        )}

        {FEATURES.map(feature => {
          const on = Boolean(pkg[feature.key])
          return (
            <div
              key={feature.key}
              className={`${classes.featureCard} ${on ? classes.featureCardOn : ''}`}>
              <div className={classes.featureHead}>
                <span className={classes.featIcon}>
                  <Glyph paths={feature.icon} />
                </span>
                <div className={classes.featText}>
                  <h4 className={classes.featTitle}>{feature.title}</h4>
                  <p className={classes.featDesc}>{feature.desc}</p>
                </div>
                {this.renderFeatureToggle(on, checked => {
                  // Turning Supervision on with no session type seeds Default so
                  // both cards cannot sit unchecked at the same time.
                  if (feature.key === 'supervision' && checked) {
                    const modes =
                      (pkg.supervisionModes || []).length > 0
                        ? pkg.supervisionModes
                        : [SUPERVISION_MODE.DEFAULT]
                    this.patchSelected({ supervision: true, supervisionModes: modes })
                    return
                  }
                  this.patchSelected({ [feature.key]: checked })
                })}
              </div>

              {feature.key === 'supervision' && on && (
                <div className={classes.subRow}>
                  <span className={classes.subLabel}>Session type</span>
                  <div className={classes.modeList} style={{ columnGap: 16 }}>
                    {SUPERVISION_MODE_OPTIONS.map(mode => {
                      const modeOn = (pkg.supervisionModes || []).includes(mode)
                      return (
                        <button
                          key={mode}
                          type="button"
                          aria-pressed={modeOn}
                          className={`${classes.modeCard} ${modeOn ? classes.modeCardOn : ''}`}
                          onClick={() => this.toggleMode(mode)}>
                          <span
                            className={`${classes.box} ${modeOn ? classes.boxChecked : ''}`}
                            aria-hidden="true">
                            {modeOn && <Glyph paths={ICON.check} size={12} strokeWidth={3} />}
                          </span>
                          <span>
                            <span className={classes.modeName}>{SUPERVISION_MODE_LABEL[mode]}</span>
                            <span className={classes.modeDesc}>{SUPERVISION_MODE_DESC[mode]}</span>
                          </span>
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}

              {feature.key === 'carePlanAudit' && on && (
                <React.Fragment>
                  <div className={classes.limitRow}>
                    <span className={classes.limitLabel}>Monthly limit</span>
                    <div
                      className={`${classes.stepper} ${pkg.auditUnlimited ? classes.stepperDisabled : ''}`}>
                      <button
                        type="button"
                        className={classes.stepBtn}
                        disabled={pkg.auditUnlimited || (auditValue !== '' && auditNum <= limitMin)}
                        aria-label="Decrease Care Plan Audit limit"
                        onClick={() => this.stepLimit(-1)}>
                        <Glyph paths={ICON.minus} size={18} />
                      </button>
                      <input
                        className={classes.stepInput}
                        type="text"
                        inputMode="numeric"
                        value={pkg.auditUnlimited ? '∞' : pkg.auditLimit}
                        disabled={pkg.auditUnlimited}
                        maxLength={String(limitMax).length}
                        aria-invalid={auditOutOfRange}
                        aria-label="Care Plan Audit monthly limit"
                        onChange={e =>
                          this.patchSelected({ auditLimit: e.target.value.replace(/[^0-9]/g, '') })
                        }
                      />
                      <button
                        type="button"
                        className={classes.stepBtn}
                        disabled={pkg.auditUnlimited || (auditValue !== '' && auditNum >= limitMax)}
                        aria-label="Increase Care Plan Audit limit"
                        onClick={() => this.stepLimit(1)}>
                        <Glyph paths={ICON.plus} size={18} />
                      </button>
                    </div>
                    <label className={classes.unlimited}>
                      <input
                        type="checkbox"
                        className={classes.hiddenInput}
                        checked={pkg.auditUnlimited}
                        onChange={e => this.patchSelected({ auditUnlimited: e.target.checked })}
                      />
                      <span
                        className={`${classes.box} ${pkg.auditUnlimited ? classes.boxChecked : ''}`}
                        aria-hidden="true">
                        {pkg.auditUnlimited && <Glyph paths={ICON.check} size={12} strokeWidth={3} />}
                      </span>
                      Unlimited
                    </label>
                  </div>
                  {auditOutOfRange && (
                    <p className={`${classes.errorText} ${classes.limitError}`}>
                      Usage limit must be between {limitMin} and {limitMax}.
                    </p>
                  )}
                </React.Fragment>
              )}
            </div>
          )
        })}

        </div>
        </div>

        {/* Fixed action bar — Cancel returns to the view, Save persists. */}
        <div className={classes.footerBar} style={{ justifyContent: 'flex-end' }}>
          <button type="button" className={classes.cancelEditBtn} onClick={this.cancelEdit}>
            Cancel
          </button>
          <button
            type="button"
            className={classes.save}
            disabled={
              !this.state.dirty || this.state.saving || this.state.impactLoading || auditLimitInvalid
            }
            onClick={this.handleSave}>
            {this.state.saving || this.state.impactLoading
              ? 'Saving…'
              : this.state.savedIds.includes(pkg.id) || this.state.savedIds.includes(String(pkg.id))
              ? 'Update package'
              : 'Save package'}
          </button>
        </div>
      </React.Fragment>
    )
  }

  render() {
    const { classes } = this.props
    const { packages, selectedId, search, statusFilter, loading, isNarrow, mobileShowDetail } =
      this.state
    const term = search.trim().toLowerCase()
    const statusOf = p => p.status || PACKAGE_STATUS.DRAFT
    const matchesSearch = p =>
      !term || (p.name || '').toLowerCase().includes(term)
    const visible = packages.filter(
      p => statusOf(p) === statusFilter && matchesSearch(p),
    )
    const selected = this.selected()
    const draftCount = packages.filter(
      p => statusOf(p) === PACKAGE_STATUS.DRAFT && matchesSearch(p),
    ).length
    const liveCount = packages.filter(
      p => statusOf(p) === PACKAGE_STATUS.LIVE && matchesSearch(p),
    ).length
    // Narrow: show one panel at a time. Wide: always show both (ignore mobile flag).
    const hideRail = isNarrow && mobileShowDetail
    const hidePane = isNarrow && !mobileShowDetail

    return (
      <div className={classes.page}>
        <div className={classes.header}>
          <button type="button" className={classes.back} onClick={this.goBack}>
            <span className={classes.backIcon}>
              <Glyph paths={ICON.arrowLeft} size={16} />
            </span>
            Back
          </button>
          <div className={classes.topMeta}>
            <span className={classes.topEyebrow}>Package builder</span>
            <span className={classes.topTitle}>QCS Organisations</span>
          </div>
        </div>

        {loading ? (
          <div className={classes.body} style={{ alignItems: 'center', justifyContent: 'center' }}>
            <p style={{ color: 'var(--careco-text-muted)', fontFamily: 'inherit', fontSize: 14 }}>
              Loading packages…
            </p>
          </div>
        ) : (
        <div className={classes.body}>
          <div className={`${classes.rail} ${hideRail ? classes.panelHidden : ''}`}>
            <div className={classes.searchWrap}>
              <span className={classes.searchIcon}>
                <Glyph paths={ICON.search} size={18} />
              </span>
              <input
                className={classes.search}
                type="text"
                placeholder="Search packages"
                value={search}
                onChange={e => this.setState({ search: e.target.value })}
              />
            </div>

            <button type="button" className={classes.newBtn} onClick={this.addPackage}>
              <span className={classes.newBtnIcon}>
                <Glyph paths={ICON.plus} size={18} />
              </span>
              New package
            </button>

            <div className={classes.railDivider} />
            <p className={classes.railHeading}>Packages</p>

            {/* Draft / Live filter chips — only when there are packages to filter. */}
            {packages.length > 0 && (
              <div className={classes.chipRow}>
              <button
                type="button"
                className={`${classes.chip} ${
                  statusFilter === PACKAGE_STATUS.LIVE ? classes.chipActive : ''
                }`}
                onClick={() => this.setState({ statusFilter: PACKAGE_STATUS.LIVE })}>
                Live <span className={classes.chipCount}>{liveCount}</span>
              </button>
              <button
                type="button"
                className={`${classes.chip} ${
                  statusFilter === PACKAGE_STATUS.DRAFT ? classes.chipActive : ''
                }`}
                onClick={() => this.setState({ statusFilter: PACKAGE_STATUS.DRAFT })}>
                Draft <span className={classes.chipCount}>{draftCount}</span>
              </button>
              </div>
            )}

            <ul className={classes.list} style={{ rowGap: 12 }}>
              {visible.map(pkg => {
                const count = enabledCount(pkg)
                return (
                  <li key={pkg.id}>
                    <div
                      role="button"
                      tabIndex={0}
                      className={`${classes.card} ${pkg.id === selectedId ? classes.cardActive : ''}`}
                      onClick={() => this.selectPackage(pkg.id)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          this.selectPackage(pkg.id)
                        }
                      }}>
                      <span className={classes.cardText}>
                        <p className={classes.cardName}>{pkg.name || 'Untitled package'}</p>
                        <p className={classes.cardMeta}>
                          {count} feature{count === 1 ? '' : 's'}
                        </p>
                      </span>
                      <button
                        type="button"
                        className={classes.cardDelete}
                        aria-label={`Delete ${pkg.name || 'package'}`}
                        onClick={e => this.requestDelete(pkg.id, e)}>
                        <Glyph paths={ICON.trash} size={16} />
                      </button>
                    </div>
                  </li>
                )
              })}
              {visible.length === 0 && (
                <li className={classes.railEmpty}>
                  {packages.length === 0
                    ? 'No packages yet. Create one with “New package”.'
                    : term
                    ? `No packages match “${search.trim()}”.`
                    : statusFilter === PACKAGE_STATUS.LIVE
                    ? 'No live packages yet. Push a draft to live.'
                    : 'No draft packages. Create one with “New package”.'}
                </li>
              )}
            </ul>
          </div>

          {selected ? (
            <div className={`${classes.pane} ${hidePane ? classes.panelHidden : ''}`}>
              {this.state.mode === 'view'
                ? this.renderView(selected)
                : this.renderEditor(selected)}
            </div>
          ) : (
            <div className={`${classes.emptyPane} ${hidePane ? classes.panelHidden : ''}`}>
              <p className={classes.emptyTitle}>No package selected</p>
              <p className={classes.emptyText}>Create a package to choose the features it unlocks.</p>
              <button type="button" className={classes.emptyBtn} onClick={this.addPackage}>
                <Glyph paths={ICON.plus} size={18} strokeWidth={2.4} />
                New package
              </button>
            </div>
          )}
        </div>
        )}

        {this.state.deleteId && (
          <Modal
            open
            onClose={this.cancelDelete}
            classes={{ paper: classes.confirmPaper }}
            tabIndex="-1">
            <div className={classes.confirmBody}>
              <h2 className={classes.confirmHeader}>Delete package</h2>
              <p className={classes.confirmText}>
                Are you sure you want to delete
                {(() => {
                  const p = packages.find(pkg => pkg.id === this.state.deleteId)
                  const name = p && p.name.trim() ? ` “${p.name.trim()}”` : ' this package'
                  return `${name}? This can’t be undone.`
                })()}
              </p>
              <div className={classes.confirmActions}>
                <button type="button" className={classes.confirmCancel} onClick={this.cancelDelete}>
                  Cancel
                </button>
                <button type="button" className={classes.confirmDelete} onClick={this.confirmDelete}>
                  Yes, delete
                </button>
              </div>
            </div>
          </Modal>
        )}

        {this.state.editImpact && (
          <Modal
            open
            onClose={this.cancelEditImpact}
            classes={{ paper: classes.impactPaper }}
            style={{ overflowY: 'hidden' }}
            tabIndex="-1">
            {(() => {
              const impact = this.state.editImpact
              const body = this.state.pendingUpdateBody || {}
              const botsToUpdate = impact.botsToUpdate || []
              const botsToSkip = impact.botsToSkip || []
              const newLimit =
                body.usageLimit?.enabled && !body.usageLimit?.unlimited
                  ? Number(body.usageLimit.limit)
                  : null
              const usageRiskBots = [...botsToUpdate, ...botsToSkip].filter(
                b =>
                  newLimit != null &&
                  Number.isFinite(newLimit) &&
                  typeof b.usageConsumed === 'number' &&
                  b.usageConsumed > newLimit,
              )
              const botLabel = b => (b.botName && String(b.botName).trim()) || `Bot ${b.botId}`
              const retainedLabel = retained => {
                if (!retained) return ''
                const parts = []
                if (retained.supervision) parts.push('supervision')
                if (retained.carePlan) parts.push('care plan')
                if (retained.usageLimit) parts.push('usage limit')
                return parts.length ? ` (keeping ${parts.join(', ')})` : ''
              }
              return (
                <div className={`${classes.confirmBody} ${classes.impactBody}`}>
                  <h2 className={classes.confirmHeader}>Update package?</h2>
                  <div className={classes.impactScroll}>
                    <p className={classes.confirmText}>
                      Updating{' '}
                      <strong>{impact.packageName || 'this package'}</strong> may affect{' '}
                      {typeof impact.assignedBotCount === 'number'
                        ? impact.assignedBotCount
                        : botsToUpdate.length + botsToSkip.length}{' '}
                      organisation
                      {(impact.assignedBotCount === 1 ? '' : 's')}. Review the impact before
                      confirming.
                    </p>

                    <div className={classes.impactSection}>
                      <p className={classes.impactSectionTitle}>
                        The following organisations will be affected:
                      </p>
                      {botsToUpdate.length === 0 ? (
                        <p className={classes.impactMuted}>No organisations will be updated.</p>
                      ) : (
                        <ul className={classes.impactList}>
                          {botsToUpdate.map(b => (
                            <li key={b.botId}>{botLabel(b)}</li>
                          ))}
                        </ul>
                      )}
                    </div>

                    <div className={classes.impactSection}>
                      <p className={classes.impactSectionTitle}>
                        {botsToSkip.length} organisations skipped to preserve manual configurations
                      </p>
                      {botsToSkip.length === 0 ? (
                        <p className={classes.impactMuted}>None.</p>
                      ) : (
                        <ul className={classes.impactList}>
                          {botsToSkip.map(b => (
                            <li key={b.botId}>
                              {botLabel(b)}
                              {retainedLabel(b.retained)}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                    {usageRiskBots.length > 0 && (
                      <p className={classes.impactWarn}>
                        The new usage limit ({newLimit}) is below current consumption for:{' '}
                        {usageRiskBots.map(botLabel).join(', ')}.
                      </p>
                    )}
                  </div>

                  <div className={classes.confirmActions}>
                    <button
                      type="button"
                      className={classes.confirmCancel}
                      onClick={this.cancelEditImpact}
                      disabled={this.state.saving}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className={classes.confirmPrimary}
                      onClick={this.confirmEditImpact}
                      disabled={this.state.saving}>
                      {this.state.saving ? 'Updating…' : 'Confirm update'}
                    </button>
                  </div>
                </div>
              )
            })()}
          </Modal>
        )}
      </div>
    )
  }
}

PackageBuilder.propTypes = {
  classes: PropTypes.object,
  history: PropTypes.object,
}

export default withRouter(withStyles(styles)(PackageBuilder))
